import { supabase } from './supabase';
import { createLogger } from './logger';
import { FeatureFlags, FILE_SERVICE_FLAGS } from './featureFlags';
import { withRetry, withRetryAndFallback, RetryOptions } from './retryUtils';
import { 
  FileMetadata,
  FileUsage,
  FileServiceError,
  QuotaExceededError,
  ThumbnailGenerationError,
  STORAGE_CONFIG,
  FileUsageType,
  FileUsageContext
} from '../types/files';
import * as pdfjsLib from 'pdfjs-dist';
import { PostgrestError } from '@supabase/supabase-js';
import { User } from '@supabase/supabase-js';

const logger = createLogger({ module: 'modernFileService' });

// Add auth caching for utility functions
// This follows the approach in the migration plan for utilities
const AUTH_CACHE_DURATION = 60000; // 1 minute cache
let cachedUser: { user: User | null; timestamp: number } | null = null;
let pendingAuthPromise: Promise<{ user: User | null }> | null = null;

/**
 * Cached auth function to reduce Supabase calls
 * This is designed for utility classes that can't use React hooks
 */
const getCachedUser = async (): Promise<{ user: User | null }> => {
  const now = Date.now();
  
  // Return cached user if valid
  if (cachedUser && (now - cachedUser.timestamp < AUTH_CACHE_DURATION)) {
    return { user: cachedUser.user };
  }
  
  // If there's already a pending request, wait for it
  if (pendingAuthPromise) {
    return pendingAuthPromise;
  }
  
  // Create a new request
  pendingAuthPromise = (async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      
      if (error) {
        logger.error('Error in getCachedUser:', error);
        cachedUser = { user: null, timestamp: now };
        return { user: null };
      }
      
      cachedUser = { user: data.user, timestamp: now };
      return { user: data.user };
    } catch (error) {
      logger.error('Exception in getCachedUser:', error);
      cachedUser = { user: null, timestamp: now };
      return { user: null };
    } finally {
      pendingAuthPromise = null;
    }
  })();
  
  return pendingAuthPromise;
};

// Storage bucket configuration
const STORAGE_BUCKETS = {
  WORK_EXPERIENCE: 'work-experience-documents',
  RESUMES: 'resumes',
  THUMBNAILS: 'thumbnails'
} as const;

// Retry configurations for different operations
const UPLOAD_RETRY_OPTIONS: Partial<RetryOptions> = {
  maxAttempts: 3,
  initialDelayMs: 2000,
  maxDelayMs: 15000
};

const DOWNLOAD_RETRY_OPTIONS: Partial<RetryOptions> = {
  maxAttempts: 3,
  initialDelayMs: 1000,
  maxDelayMs: 5000
};

const THUMBNAIL_RETRY_OPTIONS: Partial<RetryOptions> = {
  maxAttempts: 2,
  initialDelayMs: 1000,
  maxDelayMs: 3000
};

interface FileUploadOptions {
  generateThumbnail?: boolean;
  validateQuota?: boolean;
  usage?: FileUsage;
  displayLabel?: string;
}

interface ListFilesOptions {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  filters?: {
    userId?: string;
    mimeType?: string;
    usageType?: string;
    usageContext?: string;
    searchQuery?: string;
  };
}

interface ListFilesResponse {
  files: FileMetadata[];
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

interface StorageQuotaInfo {
  usagePercentage: number;
  usedSize: number;
  totalSize: number;
  availableSize: number;
}

// Helper function to safely access global Statsig instance
const getStatsig = () => {
  if (typeof window !== 'undefined' && (window as any).statsig) {
    return (window as any).statsig;
  }
  return null;
};

// Helper functions for Statsig tracking
const trackFeatureUsage = (featureName: string, action: string, metadata?: any) => {
  try {
    const statsig = getStatsig();
    if (statsig) {
      statsig.logEvent('feature_usage', {
        feature_name: featureName,
        action,
        ...metadata
      });
    }
  } catch (error) {
    logger.debug('Statsig tracking failed:', { error: error instanceof Error ? error.message : 'Unknown error' });
  }
};

const trackFileOperation = (operation: string, metadata?: any) => {
  try {
    const statsig = getStatsig();
    if (statsig) {
      statsig.logEvent('file_operation', {
        operation,
        ...metadata
      });
    }
  } catch (error) {
    logger.debug('Statsig tracking failed:', { error: error instanceof Error ? error.message : 'Unknown error' });
  }
};

export class ModernFileService {
  private static instance: ModernFileService;
  
  // Add cache for files and request throttling
  private filesCache: { [cacheKey: string]: { data: ListFilesResponse; timestamp: number } } = {};
  private lastFilesFetchTime: number = 0;
  private MIN_FILES_REQUEST_INTERVAL = 5000; // 5 seconds between requests
  private CACHE_TTL = 30000; // 30 seconds cache lifetime
  private pendingFilesRequest: Promise<ListFilesResponse> | null = null;
  
  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): ModernFileService {
    if (!ModernFileService.instance) {
      ModernFileService.instance = new ModernFileService();
    }
    return ModernFileService.instance;
  }

  private getStorageBucket(fileType?: string): string {
    if (!fileType) return STORAGE_BUCKETS.RESUMES;
    
    switch (fileType) {
      case 'work_experience_document':
        return STORAGE_BUCKETS.WORK_EXPERIENCE;
      case 'resume':
        return STORAGE_BUCKETS.RESUMES;
      default:
        return STORAGE_BUCKETS.RESUMES;
    }
  }

  // Add after the STORAGE_BUCKETS constant
  private readonly THUMBNAIL_CONFIG = {
    width: 300,
    height: 300,
    quality: 80
  };

  // Core file operations
  async uploadFile(file: File, options: FileUploadOptions = {}): Promise<FileMetadata> {
    const startTime = Date.now();
    try {
      // Check feature flags
      const [useModernService, enableQuota, enableThumbnails] = await Promise.all([
        FeatureFlags.isEnabled(FILE_SERVICE_FLAGS.USE_MODERN_SERVICE),
        FeatureFlags.isEnabled(FILE_SERVICE_FLAGS.ENABLE_QUOTA),
        FeatureFlags.isEnabled(FILE_SERVICE_FLAGS.ENABLE_THUMBNAILS)
      ]);

      if (!useModernService) {
        logger.debug('Modern file service disabled, falling back to legacy implementation');
        const result = await this.legacyUploadFile(file);
        trackFileOperation('upload', {
          file_size: file.size,
          file_type: file.type,
          duration_ms: Date.now() - startTime,
          success: true,
          legacy_fallback: true
        });
        return result;
      }

      // Validate file
      await this.validateFile(file);

      // Check quota if enabled
      if (enableQuota && options.validateQuota) {
        await this.validateStorageQuota(file.size);
      }

      // Generate storage path
      const { user } = await getCachedUser();
      if (!user) throw new Error('User not authenticated');
      
      const storagePath = this.generateStoragePath(file.name, user.id);

      // Get the appropriate storage bucket based on usage type
      const storageBucket = this.getStorageBucket(options.usage?.type);

      // Upload file with retry
      const { data: uploadData, error: uploadError } = await withRetry<{
        data: { path: string } | null;
        error: Error | null;
      }>(
        () => supabase.storage
          .from(storageBucket)
          .upload(storagePath, file, {
            cacheControl: '3600',
            upsert: false
          }),
        UPLOAD_RETRY_OPTIONS
      );

      if (uploadError) throw uploadError;

      // Create file record
      const fileRecord: Partial<FileMetadata> = {
        user_id: user.id,
        file_name: file.name,
        file_path: uploadData?.path,
        mime_type: file.type,
        size: file.size,
        usage: options.usage || {
          type: 'other' as FileUsageType,
          context: 'other' as FileUsageContext
        },
        source: 'local'
      };

      // Generate thumbnail if enabled
      if (enableThumbnails && options.generateThumbnail) {
        const thumbnailStartTime = Date.now();
        try {
          fileRecord.thumbnail_status = 'pending';
          const thumbnailUrl = await withRetry(
            () => this.generateThumbnail(file),
            THUMBNAIL_RETRY_OPTIONS
          );
          fileRecord.thumbnail_url = thumbnailUrl;
          fileRecord.thumbnail_status = 'generated';
          fileRecord.thumbnail_generated_at = new Date().toISOString();
          trackFileOperation('thumbnail_generation', {
            file_size: file.size,
            file_type: file.type,
            duration_ms: Date.now() - thumbnailStartTime,
            success: true
          });
        } catch (error) {
          logger.error('Failed to generate thumbnail:', { error, fileName: file.name });
          fileRecord.thumbnail_status = 'failed';
          trackFileOperation('thumbnail_generation', {
            file_size: file.size,
            file_type: file.type,
            duration_ms: Date.now() - thumbnailStartTime,
            success: false,
            error_message: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

      // Insert file record with retry
      const { data: fileData, error: fileError } = await withRetry<{
        data: FileMetadata;
        error: Error | null;
      }>(
        async () => {
          const result = await supabase
            .from('files')
            .insert(fileRecord)
            .select()
            .single();
          
          return {
            data: result.data as FileMetadata,
            error: this.handleSupabaseError(result.error)
          };
        },
        UPLOAD_RETRY_OPTIONS
      );

      if (fileError) throw fileError;

      trackFileOperation('upload', {
        file_size: file.size,
        file_type: file.type,
        duration_ms: Date.now() - startTime,
        success: true
      });

      // If it's a Google Drive file, get the thumbnail
      if (fileData.source === 'google_drive') {
        try {
          await this.ensureExternalThumbnail(fileData);
        } catch (error) {
          logger.error('Failed to get external thumbnail during upload:', error);
          // Continue without thumbnail - we'll try again during view
        }
      }

      // Track the operation
      trackFileOperation('google_drive_upload', {
        duration_ms: performance.now() - startTime,
        success: true,
        file_id: fileData.id
      });

      return fileData;
    } catch (error) {
      logger.error('Error uploading file:', { error, fileName: file.name });
      trackFileOperation('upload', {
        file_size: file.size,
        file_type: file.type,
        duration_ms: Date.now() - startTime,
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
      throw new FileServiceError(
        'Failed to upload file',
        error instanceof Error ? error : new Error('Unknown error')
      );
    }
  }

  async deleteFile(file: FileMetadata): Promise<void> {
    const startTime = Date.now();
    try {
      const useModernService = await FeatureFlags.isEnabled(FILE_SERVICE_FLAGS.USE_MODERN_SERVICE);
      
      if (!useModernService) {
        await this.legacyDeleteFile(file);
        trackFileOperation('delete', {
          file_id: file.id,
          file_size: file.size,
          duration_ms: Date.now() - startTime,
          success: true,
          legacy_fallback: true
        });
        return;
      }

      // Get the appropriate storage bucket based on usage type
      const storageBucket = this.getStorageBucket(file.usage?.type);

      // Delete from storage if file exists
      if (file.file_path) {
        const { error: storageError } = await supabase.storage
          .from(storageBucket)
          .remove([file.file_path]);

        if (storageError) {
          logger.error('Error deleting file from storage:', { error: storageError, fileId: file.id });
        }
      }

      // Soft delete the file record with retry
      const { error: updateError } = await withRetry<{
        data: null;
        error: Error | null;
      }>(
        async () => {
          const result = await supabase
            .from('files')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', file.id);
          
          return {
            data: null,
            error: this.handleSupabaseError(result.error)
          };
        },
        UPLOAD_RETRY_OPTIONS
      );

      if (updateError) throw updateError;

      trackFileOperation('delete', {
        file_id: file.id,
        file_size: file.size,
        duration_ms: Date.now() - startTime,
        success: true
      });
    } catch (error) {
      logger.error('Error deleting file:', { error, fileId: file.id });
      trackFileOperation('delete', {
        file_id: file.id,
        file_size: file.size,
        duration_ms: Date.now() - startTime,
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
      throw new FileServiceError(
        'Failed to delete file',
        error instanceof Error ? error : new Error('Unknown error')
      );
    }
  }

  async getFileUrl(file: FileMetadata): Promise<string> {
    const startTime = Date.now();
    try {
      const useModernService = await FeatureFlags.isEnabled(FILE_SERVICE_FLAGS.USE_MODERN_SERVICE);
      
      if (!useModernService) {
        const url = await this.legacyGetFileUrl(file);
        trackFileOperation('download', {
          file_id: file.id,
          file_size: file.size,
          duration_ms: Date.now() - startTime,
          success: true,
          legacy_fallback: true
        });
        return url;
      }

      if (file.source === 'google_drive') {
        return this.getGoogleDriveUrl(file);
      }

      // Get signed URL with retry
      const { data, error } = await withRetryAndFallback<{
        data: { signedUrl: string };
        error: Error | null;
      }>(
        async () => {
          const storageBucket = this.getStorageBucket(file.usage?.type);
          const result = await supabase.storage
            .from(storageBucket)
            .createSignedUrl(file.file_path!, 3600);
          
          if (!result.data) {
            throw new Error('Failed to create signed URL');
          }
          
          return {
            data: { signedUrl: result.data.signedUrl },
            error: null
          };
        },
        async () => {
          const url = await this.legacyGetFileUrl(file);
          return {
            data: { signedUrl: url },
            error: null
          };
        },
        DOWNLOAD_RETRY_OPTIONS
      );

      if (error) throw error;
      trackFileOperation('download', {
        file_id: file.id,
        file_size: file.size,
        duration_ms: Date.now() - startTime,
        success: true
      });
      return data.signedUrl;
    } catch (error) {
      logger.error('Error getting file URL:', { error, fileId: file.id });
      trackFileOperation('download', {
        file_id: file.id,
        file_size: file.size,
        duration_ms: Date.now() - startTime,
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
      throw new FileServiceError(
        'Failed to get file URL',
        error instanceof Error ? error : new Error('Unknown error')
      );
    }
  }

  // Expose thumbnail generation for background processing
  public async generateThumbnail(file: File): Promise<string> {
    if (file.type === 'application/pdf') {
      return this.generatePdfThumbnail(file);
    }

    if (file.type.startsWith('image/')) {
      return this.generateImageThumbnail(file);
    }

    throw new ThumbnailGenerationError(
      'Unsupported file type for thumbnail generation',
      file.name
    );
  }

  private async generatePdfThumbnail(file: File): Promise<string> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      
      // Configure PDF.js 5.x options
      const pdf = await pdfjsLib.getDocument({
        data: arrayBuffer,
        useWasm: true,
        wasmUrl: '/wasm/',
        iccUrl: '/icc/'
      }).promise;
      
      const page = await pdf.getPage(1);
      
      const viewport = page.getViewport({ scale: 1.0 });
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Scale to desired thumbnail size
      const scale = STORAGE_CONFIG.THUMBNAIL.maxWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;

      await page.render({
        canvasContext: ctx,
        viewport: scaledViewport
      }).promise;

      // Convert to blob
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob(
          (blob) => resolve(blob!),
          'image/jpeg',
          STORAGE_CONFIG.THUMBNAIL.quality / 100
        );
      });

      // Upload thumbnail
      const thumbnailPath = `thumbnails/${file.name}_thumb.jpg`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('thumbnails')
        .upload(thumbnailPath, blob, {
          contentType: 'image/jpeg',
          cacheControl: '31536000' // 1 year
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = await supabase.storage
        .from('thumbnails')
        .getPublicUrl(uploadData.path);

      return urlData.publicUrl;
    } catch (error) {
      logger.error('Error generating PDF thumbnail:', { error, fileName: file.name });
      throw new ThumbnailGenerationError(
        'Failed to generate PDF thumbnail',
        file.name
      );
    }
  }

  private async generateImageThumbnail(file: File): Promise<string> {
    try {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = objectUrl;
      });

      URL.revokeObjectURL(objectUrl);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Calculate scaled dimensions
      const scale = STORAGE_CONFIG.THUMBNAIL.maxWidth / img.width;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      // Draw scaled image
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Convert to blob
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob(
          (blob) => resolve(blob!),
          'image/jpeg',
          STORAGE_CONFIG.THUMBNAIL.quality / 100
        );
      });

      // Upload thumbnail
      const thumbnailPath = `thumbnails/${file.name}_thumb.jpg`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('thumbnails')
        .upload(thumbnailPath, blob, {
          contentType: 'image/jpeg',
          cacheControl: '31536000' // 1 year
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = await supabase.storage
        .from('thumbnails')
        .getPublicUrl(uploadData.path);

      return urlData.publicUrl;
    } catch (error) {
      logger.error('Error generating image thumbnail:', { error, fileName: file.name });
      throw new ThumbnailGenerationError(
        'Failed to generate image thumbnail',
        file.name
      );
    }
  }

  // Validation and utility methods
  private async validateFile(file: File): Promise<void> {
    // Get user type
    const { user } = await getCachedUser();
    if (!user) throw new Error('User not authenticated');

    // Get user type from users table
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('user_type')
      .eq('id', user.id)
      .single();

    if (userError) throw userError;

    // Get max file size based on user type
    const maxFileSize = userData.user_type === 'admin' 
      ? STORAGE_CONFIG.MAX_FILE_SIZE.admin 
      : STORAGE_CONFIG.MAX_FILE_SIZE.default;

    // Check file size
    if (file.size > maxFileSize) {
      const maxSizeMB = maxFileSize / (1024 * 1024);
      throw new FileServiceError(`File size exceeds maximum limit of ${maxSizeMB.toFixed(0)}MB`);
    }

    // Check mime type
    const allowedTypes = STORAGE_CONFIG.ALLOWED_MIME_TYPES.resumes;
    if (!allowedTypes.includes(file.type)) {
      throw new FileServiceError(`File type ${file.type} is not allowed`);
    }
  }

  private async validateStorageQuota(fileSize: number): Promise<void> {
    const { user } = await getCachedUser();
    if (!user) throw new Error('User not authenticated');

    // Get user type from users table
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('user_type')
      .eq('id', user.id)
      .single();

    if (userError) throw userError;

    // Get max file size based on user type
    const maxFileSize = userData.user_type === 'admin' 
      ? STORAGE_CONFIG.MAX_FILE_SIZE.admin 
      : STORAGE_CONFIG.MAX_FILE_SIZE.default;

    // Get current storage usage
    const { data: files, error } = await supabase
      .from('files')
      .select('size')
      .eq('user_id', user.id)
      .is('deleted_at', null);

    if (error) throw error;

    const currentUsage = files.reduce((total, file) => total + (file.size || 0), 0);
    const newUsage = currentUsage + fileSize;

    // Track quota metrics
    trackFileOperation('quota_check', {
      user_id: user.id,
      total_bytes: maxFileSize,
      used_bytes: currentUsage,
      available_bytes: maxFileSize - currentUsage,
      usage_percentage: (currentUsage / maxFileSize) * 100
    });

    if (newUsage > maxFileSize) {
      throw new QuotaExceededError('Storage quota exceeded', {
        used_bytes: currentUsage,
        total_bytes: maxFileSize,
        available_bytes: maxFileSize - currentUsage
      });
    }
  }

  private generateStoragePath(fileName: string, userId: string): string {
    const fileExt = fileName.split('.').pop() || '';
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    return `${userId}/${timestamp}-${randomString}.${fileExt}`;
  }

  // Legacy fallback methods
  private async legacyUploadFile(file: File): Promise<FileMetadata> {
    logger.debug('Using legacy upload implementation', { fileName: file.name });
    
    // Validate file using legacy validation
    if (!STORAGE_CONFIG.ALLOWED_MIME_TYPES.resumes.includes(file.type)) {
      throw new FileServiceError(`File type ${file.type} is not supported`);
    }

    const MAX_SIZE = STORAGE_CONFIG.MAX_FILE_SIZE.default; // Use the default size limit
    if (file.size > MAX_SIZE) {
      throw new FileServiceError(`File size exceeds limit of ${MAX_SIZE / 1024 / 1024}MB`);
    }

    // Generate storage path using legacy method
    const { user } = await getCachedUser();
    if (!user) throw new Error('User must be authenticated to upload files');
    
    const fileExt = file.name.split('.').pop() || '';
    const randomId = Math.random().toString(36).substring(2);
    const storagePath = `${user.id}/${randomId}.${fileExt}`;

    // Upload file using legacy bucket
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('resumes')
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    // Create legacy file record
    const fileRecord: Partial<FileMetadata> = {
      user_id: user.id,
      file_name: file.name,
      file_path: uploadData?.path,
      mime_type: file.type,
      size: file.size,
      usage: {
        type: 'resume',
        context: 'resume_upload'
      },
      source: 'local'
    };

    // Insert file record
    const { data: fileData, error: fileError } = await supabase
      .from('files')
      .insert(fileRecord)
      .select()
      .single();

    if (fileError) throw fileError;

    return fileData;
  }

  private async legacyDeleteFile(file: FileMetadata): Promise<void> {
    logger.debug('Using legacy delete implementation', { fileId: file.id });
    
    if (!file.file_path) {
      throw new FileServiceError('File path not found');
    }

    // Legacy implementation used physical deletion
    const { error: deleteError } = await supabase.storage
      .from('resumes')
      .remove([file.file_path]);

    if (deleteError) throw deleteError;

    // Delete file record
    const { error: recordError } = await supabase
      .from('files')
      .delete()
      .eq('id', file.id);

    if (recordError) throw recordError;
  }

  private async legacyGetFileUrl(file: FileMetadata): Promise<string> {
    logger.debug('Using legacy URL generation implementation', { fileId: file.id });

    if (!file.file_path) {
      throw new FileServiceError('File path not found');
    }

    // Legacy implementation used 1-hour signed URLs
    const storageBucket = this.getStorageBucket(file.usage?.type);
    const { data, error } = await supabase.storage
      .from(storageBucket)
      .createSignedUrl(file.file_path, 3600);

    if (error) throw error;
    return data.signedUrl;
  }

  private async getGoogleDriveUrl(file: FileMetadata & { external_web_url?: string }): Promise<string> {
    // Check both new and legacy URL fields
    const url = file.source_metadata?.web_view_url || file.external_web_url;
    if (!url || typeof url !== 'string') {
      throw new FileServiceError('Google Drive URL not available');
    }
    return url;
  }

  private handleSupabaseError(error: PostgrestError | null): Error | null {
    if (!error) return null;
    const err = new Error(error.message || 'Unknown database error');
    err.name = 'PostgrestError';
    return err;
  }

  // Update getThumbnailUrl method to generate thumbnail if needed
  public async getThumbnailUrl(file: FileMetadata): Promise<string | null> {
    try {
      // For Google Drive files
      if (file.source === 'google_drive') {
        logger.debug('Processing Google Drive file:', {
          fileId: file.id,
          fileName: file.file_name,
          externalFileId: file.external_file_id,
          thumbnailStatus: file.external_thumbnail_status,
          currentThumbnailUrl: file.external_thumbnail_url
        });

        if (!file.external_file_id) {
          logger.error('Google Drive file missing external_file_id:', {
            fileId: file.id,
            fileName: file.file_name
          });
          return null;
        }

        // Use the export=view parameter which doesn't require authentication
        const thumbnailUrl = `https://drive.google.com/thumbnail?id=${file.external_file_id}&sz=w300-h300&export=view`;
        
        logger.debug('Generated Google Drive thumbnail URL:', {
          fileId: file.id,
          thumbnailUrl
        });
        
        // Update the file record with the new thumbnail URL
        const { error: updateError } = await supabase.from('files')
          .update({
            external_thumbnail_url: thumbnailUrl,
            external_thumbnail_status: 'generated',
            external_thumbnail_generated_at: new Date().toISOString()
          })
          .eq('id', file.id);

        if (updateError) {
          logger.error('Error updating thumbnail URL:', updateError);
          return null;
        }

        return thumbnailUrl;
      }

      // For local files
      if (file.thumbnail_url) {
        try {
          // Extract the path from the public URL
          const urlPath = new URL(file.thumbnail_url).pathname;
          const pathParts = urlPath.split('/');
          const thumbnailPath = pathParts.slice(pathParts.indexOf('thumbnails') + 1).join('/');

          // Get a signed URL for the thumbnail
          const { data, error } = await supabase.storage
            .from('thumbnails')
            .createSignedUrl(thumbnailPath, 3600); // 1 hour expiry

          if (error) throw error;
          return data.signedUrl;
        } catch (error) {
          logger.error('Error getting signed thumbnail URL:', error);
          // If there's an error, try regenerating the thumbnail
          return this.generateThumbnailForExisting(file);
        }
      }

      // If no thumbnail exists, generate one
      return this.generateThumbnailForExisting(file);
    } catch (error) {
      logger.error('Error getting thumbnail URL:', error);
      return null;
    }
  }

  // Add method to generate thumbnail for existing file
  private async generateThumbnailForExisting(file: FileMetadata): Promise<string | null> {
    try {
      // Get the file URL
      const fileUrl = await this.getFileUrl(file);
      
      // For PDFs
      if (file.mime_type === 'application/pdf') {
        // Fetch the PDF file
        const response = await fetch(fileUrl);
        const arrayBuffer = await response.arrayBuffer();
        
        // Load the PDF
        const pdf = await pdfjsLib.getDocument({
          data: arrayBuffer,
          useWasm: true,
          wasmUrl: '/wasm/',
          iccUrl: '/icc/'
        }).promise;
        const page = await pdf.getPage(1);
        
        const viewport = page.getViewport({ scale: 1.0 });
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        // Scale to desired thumbnail size
        const scale = Math.min(
          STORAGE_CONFIG.THUMBNAIL.maxWidth / viewport.width,
          STORAGE_CONFIG.THUMBNAIL.maxHeight / viewport.height
        );
        const scaledViewport = page.getViewport({ scale });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        await page.render({
          canvasContext: ctx,
          viewport: scaledViewport
        }).promise;

        // Convert to blob
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob(
            (blob) => resolve(blob!),
            'image/jpeg',
            STORAGE_CONFIG.THUMBNAIL.quality / 100
          );
        });

        return this.uploadAndSaveThumbnail(file, blob);
      }
      
      // For images
      if (file.mime_type.startsWith('image/')) {
        // Get transformed URL using Supabase's image transformation
        const { data: urlData } = await supabase.storage
          .from(this.getStorageBucket(file.usage?.type))
          .createSignedUrl(file.file_path!, 3600, {
            transform: {
              width: STORAGE_CONFIG.THUMBNAIL.maxWidth,
              height: STORAGE_CONFIG.THUMBNAIL.maxHeight,
              resize: 'contain',
              quality: STORAGE_CONFIG.THUMBNAIL.quality,
            }
          });

        if (!urlData?.signedUrl) {
          throw new Error('Failed to get transformed image URL');
        }

        // Fetch the transformed image
        const response = await fetch(urlData.signedUrl);
        const blob = await response.blob();

        return this.uploadAndSaveThumbnail(file, blob);
      }

      return null;
    } catch (error) {
      logger.error('Error generating thumbnail for existing file:', error);
      return null;
    }
  }

  // Helper method to upload thumbnail and update file record
  private async uploadAndSaveThumbnail(file: FileMetadata, blob: Blob): Promise<string | null> {
    try {
      // Upload thumbnail
      const thumbnailPath = `${file.user_id}/${file.id}_thumb.jpg`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('thumbnails')
        .upload(thumbnailPath, blob, {
          contentType: 'image/jpeg',
          cacheControl: '31536000', // 1 year
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = await supabase.storage
        .from('thumbnails')
        .getPublicUrl(uploadData.path);

      // Update file record
      const { error: updateError } = await supabase
        .from('files')
        .update({
          thumbnail_url: urlData.publicUrl,
          thumbnail_status: 'generated',
          thumbnail_generated_at: new Date().toISOString()
        })
        .eq('id', file.id);

      if (updateError) throw updateError;

      return urlData.publicUrl;
    } catch (error) {
      logger.error('Error uploading and saving thumbnail:', error);
      return null;
    }
  }

  // Update getGoogleDriveThumbnail method
  private async getGoogleDriveThumbnail(fileId: string, accessToken: string): Promise<string> {
    try {
      // First try to refresh the access token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.provider_token) {
        throw new Error('No provider token available');
      }

      // Use the refreshed token from the session
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?fields=thumbnailLink`,
        {
          headers: {
            'Authorization': `Bearer ${session.provider_token}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, try to refresh the session
          const { data: { session: newSession } } = await supabase.auth.refreshSession();
          if (!newSession?.provider_token) {
            throw new Error('Failed to refresh provider token');
          }

          // Retry with new token
          const retryResponse = await fetch(
            `https://www.googleapis.com/drive/v3/files/${fileId}?fields=thumbnailLink`,
            {
              headers: {
                'Authorization': `Bearer ${newSession.provider_token}`
              }
            }
          );

          if (!retryResponse.ok) {
            throw new Error(`Failed to get Google Drive thumbnail after token refresh: ${retryResponse.statusText}`);
          }

          const retryData = await retryResponse.json();
          return retryData.thumbnailLink || '';
        }
        throw new Error(`Failed to get Google Drive thumbnail: ${response.statusText}`);
      }

      const data = await response.json();
      return data.thumbnailLink || '';
    } catch (error) {
      logger.error('Error getting Google Drive thumbnail:', error);
      throw error;
    }
  }

  // Update ensureExternalThumbnail method to use cached auth
  private async ensureExternalThumbnail(file: FileMetadata): Promise<void> {
    if (file.source !== 'google_drive' || 
        file.external_thumbnail_url || 
        !file.external_file_id) {
      return;
    }

    try {
      // Update status to pending
      await supabase.from('files')
        .update({
          external_thumbnail_status: 'pending'
        })
        .eq('id', file.id);

      // Get the current session token
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.provider_token;

      if (!accessToken) {
        throw new Error('No access token available');
      }

      const thumbnailUrl = await this.getGoogleDriveThumbnail(
        file.external_file_id,
        accessToken
      );

      if (!thumbnailUrl) {
        throw new Error('No thumbnail URL returned');
      }

      // Update with thumbnail URL
      await supabase.from('files')
        .update({
          external_thumbnail_url: thumbnailUrl,
          external_thumbnail_status: 'generated',
          external_thumbnail_generated_at: new Date().toISOString()
        })
        .eq('id', file.id);

    } catch (error) {
      logger.error('Error ensuring external thumbnail:', error);
      
      // Update status to failed
      await supabase.from('files')
        .update({
          external_thumbnail_status: 'failed',
          external_thumbnail_generated_at: new Date().toISOString()
        })
        .eq('id', file.id);
        
      throw error;
    }
  }

  /**
   * Fetches all non-deleted resume files for a given user
   * @param userId The ID of the user whose resumes to fetch
   * @returns Array of FileMetadata objects representing resumes
   * @throws FileServiceError if the operation fails
   */
  public async getResumes(userId: string): Promise<FileMetadata[]> {
    const startTime = Date.now();
    try {
      const useModernService = await FeatureFlags.isEnabled(FILE_SERVICE_FLAGS.USE_MODERN_SERVICE);
      
      if (!useModernService) {
        logger.debug('Modern file service disabled, falling back to legacy implementation');
        return this.legacyGetResumes(userId);
      }

      const { data, error } = await withRetry<{
        data: FileMetadata[];
        error: Error | null;
      }>(
        async () => {
          const result = await supabase
            .from('files')
            .select('*')
            .eq('user_id', userId)
            .eq('usage->>type', 'resume')
            .is('deleted_at', null)
            .order('created_at', { ascending: false });
          
          return {
            data: result.data as FileMetadata[],
            error: this.handleSupabaseError(result.error)
          };
        },
        DOWNLOAD_RETRY_OPTIONS
      );

      if (error) throw error;
      
      // Track successful operation
      trackFileOperation('list_files', {
        duration_ms: Date.now() - startTime,
        success: true,
        operation_type: 'resumes'
      });
      
      return data || [];
    } catch (error) {
      logger.error('Error fetching resumes:', { error, userId });
      
      // Track failed operation
      trackFileOperation('list_files', {
        duration_ms: Date.now() - startTime,
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        operation_type: 'resumes'
      });
      
      throw new FileServiceError(
        'Failed to fetch resumes',
        error instanceof Error ? error : new Error('Unknown error')
      );
    }
  }

  private async legacyGetResumes(userId: string): Promise<FileMetadata[]> {
    logger.debug('Using legacy resume fetching implementation', { userId });
    
    const { data, error } = await supabase
      .from('files')
      .select('*')
      .eq('user_id', userId)
      .eq('usage->>type', 'resume')
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  /**
   * Creates a file record for a Google Drive file
   * Handles the integration of Google Drive files into our system
   */
  async createGoogleDriveFile(params: {
    userId: string;
    fileName: string;
    mimeType: string;
    fileId: string;
    webViewLink: string;
    webContentLink: string;
    accessToken: string;
    usage: FileUsage;
  }): Promise<FileMetadata> {
    try {
      const startTime = performance.now();
      
      // Insert the file record
      const { data, error } = await supabase
        .from('files')
        .insert({
          user_id: params.userId,
          file_name: params.fileName,
          mime_type: params.mimeType,
          source: 'google_drive',
          external_file_id: params.fileId,
          external_web_url: params.webViewLink,
          external_download_url: params.webContentLink,
          access_token: params.accessToken,
          usage: params.usage
        })
        .select()
        .single();

      if (error) {
        throw this.handleSupabaseError(error);
      }

      // Track the operation
      trackFileOperation('file_upload', {
        duration_ms: performance.now() - startTime,
        success: true,
        file_id: data.id
      });

      return data;
    } catch (error) {
      // Track failed operation
      trackFileOperation('file_upload', {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      
      throw error;
    }
  }

  /**
   * List files with pagination, sorting, and filtering support
   */
  async listFiles(options: ListFilesOptions = {}): Promise<ListFilesResponse> {
    const startTime = performance.now();
    const {
      page = 1,
      pageSize = 20,
      sortBy = 'created_at',
      sortDirection = 'desc',
      filters = {}
    } = options;

    try {
      // Get the authenticated user
      const { user } = await getCachedUser();
      if (!user) throw new Error('User not authenticated');
      
      // Create cache key based on request parameters
      const cacheKey = JSON.stringify({
        userId: filters.userId || user.id,
        page,
        pageSize,
        sortBy,
        sortDirection,
        filters
      });
      
      // Check if we have a valid cached response
      const now = Date.now();
      const cachedResponse = this.filesCache[cacheKey];
      if (cachedResponse && (now - cachedResponse.timestamp < this.CACHE_TTL)) {
        logger.debug('Using cached files list');
        return cachedResponse.data;
      }
      
      // Throttle requests - if last request was too recent, wait before making another
      const timeSinceLastRequest = now - this.lastFilesFetchTime;
      if (timeSinceLastRequest < this.MIN_FILES_REQUEST_INTERVAL) {
        logger.debug(`Throttling files request (${timeSinceLastRequest}ms since last request)`);
        
        // If there's already a pending request, wait for it instead of making a new one
        if (this.pendingFilesRequest) {
          logger.debug('Reusing pending files request');
          return this.pendingFilesRequest;
        }
        
        // If we have any cached data, return it while waiting for the next request
        if (cachedResponse) {
          logger.debug('Using slightly stale cache while throttling');
          return cachedResponse.data;
        }
        
        // If we don't have cached data, wait until we can make a new request
        await new Promise(resolve => 
          setTimeout(resolve, this.MIN_FILES_REQUEST_INTERVAL - timeSinceLastRequest)
        );
      }
      
      // Create a new files request and store it for reuse
      this.lastFilesFetchTime = now;
      this.pendingFilesRequest = this.fetchFilesFromAPI(options, user.id);
      
      try {
        const response = await this.pendingFilesRequest;
        
        // Cache the result
        this.filesCache[cacheKey] = {
          data: response,
          timestamp: now
        };
        
        return response;
      } finally {
        // Clear the pending request reference
        this.pendingFilesRequest = null;
      }
    } catch (error) {
      // Log the error
      logger.error('Error listing files:', error);

      // Track the failed operation
      trackFileOperation('list_files', {
        duration_ms: performance.now() - startTime,
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });

      // Throw a standardized error
      throw new FileServiceError(
        'Failed to list files',
        error instanceof Error ? error : new Error('Unknown error')
      );
    }
  }
  
  /**
   * Actual API call function separated for better control
   */
  private async fetchFilesFromAPI(
    options: ListFilesOptions,
    userId: string
  ): Promise<ListFilesResponse> {
    const startTime = performance.now();
    const {
      page = 1,
      pageSize = 20,
      sortBy = 'created_at',
      sortDirection = 'desc',
      filters = {}
    } = options;
  
    try {
      // Start building the query
      let query = supabase
        .from('files')
        .select('*', { count: 'exact' })
        .eq('user_id', filters.userId || userId)
        .is('deleted_at', null);

      // Apply filters if provided
      if (filters.mimeType) {
        query = query.eq('mime_type', filters.mimeType);
      }
      if (filters.usageType) {
        query = query.eq('usage->>type', filters.usageType);
      }
      if (filters.usageContext) {
        query = query.eq('usage->>context', filters.usageContext);
      }
      if (filters.searchQuery) {
        query = query.ilike('file_name', `%${filters.searchQuery}%`);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortDirection === 'asc' });

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      // Execute the query
      const { data, error, count } = await query;

      if (error) {
        throw this.handleSupabaseError(error);
      }

      // Track the operation
      trackFileOperation('list_files', {
        duration_ms: performance.now() - startTime,
        success: true,
        mime_type: filters.mimeType
      });

      const totalPages = count ? Math.ceil(count / pageSize) : 0;

      return {
        files: data || [],
        currentPage: page,
        totalPages,
        totalCount: count || 0
      };
    } catch (error) {
      logger.error('Error in fetchFilesFromAPI:', error);
      throw error;
    }
  }

  /**
   * Gets the current storage quota usage for a user
   * @param userId Optional user ID. If not provided, uses the currently authenticated user
   * @returns StorageQuotaInfo containing usage details
   */
  async getStorageQuota(userId?: string): Promise<StorageQuotaInfo> {
    try {
      // Get the user ID if not provided
      if (!userId) {
        const { user } = await getCachedUser();
        if (!user) throw new Error('User not authenticated');
        userId = user.id;
      }

      // Get user type from users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('user_type')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      // Get max file size based on user type
      const totalSize = userData.user_type === 'admin' 
        ? STORAGE_CONFIG.MAX_FILE_SIZE.admin 
        : STORAGE_CONFIG.MAX_FILE_SIZE.default;

      // Get current storage usage from files table
      const { data: files, error: filesError } = await withRetry<{
        data: { size: number | null }[];
        error: Error | null;
      }>(
        async () => {
          const result = await supabase
            .from('files')
            .select('size')
            .eq('user_id', userId)
            .is('deleted_at', null);
          
          return {
            data: result.data || [],
            error: this.handleSupabaseError(result.error)
          };
        },
        DOWNLOAD_RETRY_OPTIONS
      );

      if (filesError) throw filesError;

      // Calculate total used size
      const usedSize = files.reduce((total, file) => total + (file.size || 0), 0);
      const availableSize = totalSize - usedSize;
      const usagePercentage = (usedSize / totalSize) * 100;

      // Track quota metrics
      trackFileOperation('quota_check', {
        user_id: userId,
        total_bytes: totalSize,
        used_bytes: usedSize,
        available_bytes: availableSize,
        usage_percentage: usagePercentage
      });

      return {
        usagePercentage,
        usedSize,
        totalSize,
        availableSize
      };

    } catch (error) {
      logger.error('Error getting storage quota:', error);
      throw new FileServiceError(
        'Failed to get storage quota',
        error instanceof Error ? error : new Error('Unknown error')
      );
    }
  }

  /**
   * Updates user settings with proper error handling and telemetry
   */
  async updateUserSettings(userId: string, settings: {
    files_view_mode?: 'grid' | 'list';
    settings?: Record<string, any>;
    unified_home_settings?: Record<string, any>;
  }): Promise<void> {
    const startTime = performance.now();
    try {
      // First get existing settings
      const { data: existingSettings, error: fetchError } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found"
        throw fetchError;
      }

      // Prepare update data
      const updateData = {
        user_id: userId,
        ...settings,
        settings: {
          ...(existingSettings?.settings || {}),
          ...(settings.settings || {})
        }
      };

      // Update or insert settings with retry
      const { error: upsertError } = await withRetry<{
        data: null;
        error: Error | null;
      }>(
        async () => {
          const result = await supabase
            .from('user_settings')
            .upsert(updateData, {
              onConflict: 'user_id',
              ignoreDuplicates: false
            });
          
          return {
            data: null,
            error: this.handleSupabaseError(result.error)
          };
        },
        UPLOAD_RETRY_OPTIONS
      );

      if (upsertError) throw upsertError;

      // Track successful operation
      trackFileOperation('update_user_settings', {
        duration_ms: performance.now() - startTime,
        success: true
      });
    } catch (error) {
      logger.error('Error updating user settings:', error);
      
      // Track failed operation
      trackFileOperation('update_user_settings', {
        duration_ms: performance.now() - startTime,
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
      
      throw new FileServiceError(
        'Failed to update user settings',
        error instanceof Error ? error : new Error('Unknown error')
      );
    }
  }

  /**
   * Gets user settings with proper error handling
   */
  async getUserSettings(userId: string): Promise<{
    files_view_mode?: 'grid' | 'list';
    settings?: Record<string, any>;
    unified_home_settings?: Record<string, any>;
  }> {
    const startTime = performance.now();
    try {
      const { data, error } = await withRetry<{
        data: any;
        error: Error | null;
      }>(
        async () => {
          const result = await supabase
            .from('user_settings')
            .select('*')
            .eq('user_id', userId)
            .single();
          
          return {
            data: result.data,
            error: this.handleSupabaseError(result.error)
          };
        },
        DOWNLOAD_RETRY_OPTIONS
      );

      if (error) throw error;

      // Track successful operation
      trackFileOperation('get_user_settings', {
        duration_ms: performance.now() - startTime,
        success: true
      });

      return data || {};
    } catch (error) {
      logger.error('Error getting user settings:', error);
      
      // Track failed operation
      trackFileOperation('get_user_settings', {
        duration_ms: performance.now() - startTime,
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
      
      throw new FileServiceError(
        'Failed to get user settings',
        error instanceof Error ? error : new Error('Unknown error')
      );
    }
  }
} 
